import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import { legalEntityQueryConfig } from '@webapp/platform/legal-entities/query-configs/legalEntityQueryConfig'

interface UseQueryActiveLegalEntitiesReturn {
  entities: LegalEntity[]
  isFetching: boolean
  isLoading: boolean
  isError: boolean
}

/**
 * Returns only the Active Legal Entities, for the Multi-Entity selector surfaces.
 *
 * Selects off the shared `list` query config — the same request the full list
 * view uses — so React Query dedupes and the active subset stays consistent with
 * `useQueryLegalEntities`. The list endpoint returns every entity in one request;
 * the `?active=true` server-side filter is a future path if the set outgrows the
 * client, but is not needed today.
 */
export const useQueryActiveLegalEntities =
  (): UseQueryActiveLegalEntitiesReturn => {
    const { data, isFetching, isLoading, isError } = useQuery(
      legalEntityQueryConfig.list()
    )

    const entities = useMemo(
      () =>
        (data ?? [])
          .map((entity) => new LegalEntity(entity))
          .filter((entity) => entity.active),
      [data]
    )

    return {
      entities,
      isFetching,
      isLoading,
      isError,
    }
  }
